import { storage } from '../lib/firebase';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { cloudinaryUploader, uploadStudentIdToCloudinary, uploadVideoToCloudinary } from './cloudinary';
import { compressImage } from '../utils/imageCompress';

// Uploads a tutorial video straight to Firebase Storage (no ~4.5MB serverless cap),
// reporting progress. Returns the public download URL. Falls back to Cloudinary
// (also a direct browser upload, so no serverless limit) if Firebase fails/stalls.
export async function uploadTutorialVideo(
  file: File,
  slot: string,
  onProgress?: (pct: number) => void
): Promise<string> {
  const safeSlot = String(slot || 'extra').replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
  const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;
  const hasCloudinary = !!(cloudName && uploadPreset);

  let cloudErr: any = null;

  // 1) Cloudinary first — it's a direct browser upload (no serverless size limit),
  //    handles large videos, and is the same path used for marketplace videos.
  if (hasCloudinary) {
    try {
      return await uploadVideoToCloudinary(file, `tutorials/${safeSlot}`, onProgress);
    } catch (e) {
      cloudErr = e;
      // fall through to Firebase
    }
  }

  // 2) Firebase Storage resumable upload, with a stall watchdog so it can never
  //    "just load forever" — if no bytes move for 45s we cancel and report.
  try {
    return await firebaseResumableUpload(
      file,
      `tutorials/${safeSlot}/${Date.now()}-${safeName}`,
      onProgress
    );
  } catch (fbErr: any) {
    const fb = fbErr?.code || fbErr?.message || 'storage error';
    if (cloudErr) {
      throw new Error(`Upload failed. Cloudinary: ${cloudErr?.message || cloudErr}. Firebase: ${fb}.`);
    }
    throw new Error(`Upload failed: ${fb}`);
  }
}

// Firebase resumable upload with progress + a stall watchdog.
function firebaseResumableUpload(
  file: File,
  path: string,
  onProgress?: (pct: number) => void
): Promise<string> {
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type || 'video/mp4' });
  return new Promise<string>((resolve, reject) => {
    let lastBytes = 0;
    let lastTick = Date.now();
    const watchdog = setInterval(() => {
      if (Date.now() - lastTick > 45000) {
        clearInterval(watchdog);
        try { task.cancel(); } catch {}
        reject(new Error('upload stalled — no network progress for 45s'));
      }
    }, 5000);
    task.on(
      'state_changed',
      (snap) => {
        if (snap.bytesTransferred !== lastBytes) { lastBytes = snap.bytesTransferred; lastTick = Date.now(); }
        if (onProgress) onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
      },
      (err) => { clearInterval(watchdog); reject(err); },
      async () => { clearInterval(watchdog); try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (e) { reject(e); } }
    );
  });
}

export async function uploadListingImage(rawFile: File, userId: string): Promise<string> {
  // This function handles both image and video uploads for listings.
  // The underlying storage services (Vercel Blob, Cloudinary, Firebase) are configured
  // to accept both image and video formats as specified in their respective configurations.
  // Compress images first so they stay under the serverless body limit (~4.5MB) and
  // upload fast on mobile. Videos/non-images pass through unchanged.
  const file = await compressImage(rawFile);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const vercelPath = `listings/${userId}/${Date.now()}-${safeName}`;
  try {
    return await uploadViaVercelBlob(file, vercelPath);
  } catch {
    // Cloudinary fallback if configured
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
    const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;
    if (cloudName && uploadPreset) {
      try {
        const res = await cloudinaryUploader.uploadFile(file, {
          folder: `listings/${userId}`,
          allowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'mp4', 'mov', 'avi', 'webm'],
          maxFileSize: 20 * 1024 * 1024,
          transformation: { quality: 'auto', fetch_format: 'auto' },
        });
          return res.secure_url;
      } catch {}
    }
    // Firebase final fallback
    const path = `listings/${userId}/${Date.now()}-${safeName}`;
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    return await getDownloadURL(snap.ref);
  }
}

export async function uploadViaVercelBlob(file: File, pathname: string): Promise<string> {
  // Abort the upload if it stalls so the caller can fall back instead of hanging forever.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60_000);
  let res: Response;
  try {
    res = await fetch(`/api/blob-upload?pathname=${encodeURIComponent(pathname)}`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Blob upload failed: ${res.status} ${text}`);
  }

  const data = await res.json().catch(() => null);
  const url = (data as any)?.url as string | undefined;
  if (!url) {
    throw new Error('Blob upload returned no URL');
  }
  return url;
}

export async function uploadStudentIdImage(rawFile: File, userId: string): Promise<string> {
  // Compress first: a raw phone photo of an ID card often exceeds the ~4.5MB
  // serverless body limit, which made the upload throw and left sellers pending
  // with no ID showing in admin. 1600px/JPEG keeps the card fully legible.
  const file = await compressImage(rawFile);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const vercelPath = `student-ids/${userId}/${Date.now()}-${safeName}`;
  try {
    return await uploadViaVercelBlob(file, vercelPath);
  } catch {
    // Cloudinary fallback if configured
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
    const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;
    if (cloudName && uploadPreset) {
      try {
        return await uploadStudentIdToCloudinary(file, userId);
      } catch {}
    }
    // Firebase final fallback
    const path = `student-ids/${userId}/${Date.now()}-${safeName}`;
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    return await getDownloadURL(snap.ref);
  }
}

export async function uploadMessageAttachment(file: File, userId: string, conversationId: string): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const vercelPath = `messages/${conversationId}/${userId}/${Date.now()}-${safeName}`;
  try {
    return await uploadViaVercelBlob(file, vercelPath);
  } catch {
    // Cloudinary fallback if configured
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
    const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;
    if (cloudName && uploadPreset) {
      try {
        const res = await cloudinaryUploader.uploadFile(file, {
          folder: `messages/${conversationId}/${userId}`,
          allowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'mp4', 'mov', 'avi', 'webm', 'mp3', 'wav', 'm4a', 'ogg'],
          maxFileSize: 20 * 1024 * 1024,
          transformation: { quality: 'auto', fetch_format: 'auto' },
        });
        return res.secure_url;
      } catch {}
    }
    // Firebase final fallback
    const path = `messages/${conversationId}/${userId}/${Date.now()}-${safeName}`;
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    return await getDownloadURL(snap.ref);
  }
}

export async function uploadProfileImage(file: File, userId: string): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const vercelPath = `profiles/${userId}/${Date.now()}-${safeName}`;
  try {
    return await uploadViaVercelBlob(file, vercelPath);
  } catch {
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
    const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;
    if (cloudName && uploadPreset) {
      try {
        const res = await (await import('./cloudinary')).cloudinaryUploader.uploadFile(file, {
          folder: `profiles/${userId}`,
          allowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
          maxFileSize: 10 * 1024 * 1024,
          transformation: { quality: 'auto', fetch_format: 'auto' },
        });
        return res.secure_url;
      } catch {}
    }
    const { storage } = await import('../lib/firebase');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const path = `profiles/${userId}/${Date.now()}-${safeName}`;
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    return await getDownloadURL(snap.ref);
  }
}